import { observer } from 'mobx-react';
import { useBaseStore } from '@roc/feature-app-core';
import { useDocumentStore } from '@roc/feature-documents';
import { LoanProcess } from '@roc/feature-utils';
import { mapStatusFilter, SHOW_ALL_TO_DOS, SHOW_PENDING_TO_DOS, StatusFilterField, TaskTodoCardCompponent, TodoStatus } from '@roc/feature-loan-details';
import { useEffect, useRef, useState } from 'react';
import { Button, PrintableArea, PrintableAreaRef } from '@roc/ui';
import { Box, Tooltip } from '@material-ui/core';
import { PhotoCamera, UnfoldLess, UnfoldMore } from '@material-ui/icons';
import { DrawDocumentForms } from './drawDocumentForms';
import { useDrawsStore } from '../hooks';
import { TaskV2Type } from 'libs/feature-general-tasks/src/generalTasks/types/generalTasksTypes';

interface DrawTodosProps {
  loanId: number;
  drawId: number;
  isCPortal?: boolean;
  afterActionExecuted?: () => void;
}

export const DrawTodos = observer(({ loanId, drawId, isCPortal }: DrawTodosProps) => {
  const { documentStore } = useDocumentStore();
  const { drawStore } = useDrawsStore();

  const [statusFilter, setStatusFilter] = useState(isCPortal ? SHOW_PENDING_TO_DOS : SHOW_ALL_TO_DOS);
  const [filterBy, setFilterBy] = useState(mapStatusFilter[statusFilter]);
  const currentForm = documentStore.documentFormStore.currentForm;
  const loanProcess = LoanProcess.DRAWS;
  const { globalStore } = useBaseStore();
  const printableAreaRef = useRef<PrintableAreaRef>(null);
  const [expandAll, setExpandAll] = useState(0);
  const [collapseAll, setCollapseAll] = useState(0);
  const isInternalPortal = globalStore.isInternalPortal;
  const todos =
    documentStore.loanTodosStore.todosByLoanProcess[loanProcess] ??
    [];

  const loadTodos = () => {
    documentStore.loanTodosStore.fetchTodos(loanId, loanProcess, TaskV2Type.DRAW_ID, drawId);
  };

  const download = () => {
    printableAreaRef.current.downloadAsPdf(`Loan ${loanId} Draw ${drawId} Documents.pdf`, {
      singlePage: true,
    });
  };

  useEffect(() => {
    documentStore.loanTodosStore.setShowOnlyPending(isCPortal);
    documentStore.loanTodosStore.setPrioritiesFilter([]);
    loadTodos();
  }, [loanId]);

  useEffect(() => {
    if (!currentForm && todos.length > 0) {
      loadTodos();
    }
  }, [currentForm]);

  const drawTodos = todos.filter(t => t?.document?.drawId && t?.document?.drawId === drawId && (isInternalPortal || t?.document?.status !== TodoStatus.NOT_APPLICABLE));

  const filteredTodos = drawTodos.filter(t => {

    if (filterBy === TodoStatus.ALL) return t;
    if (filterBy === TodoStatus.PENDING) return t.status === filterBy || t.status === TodoStatus.MORE_INFO_NEEDED;
    return t.status === filterBy || filterBy.split(',').includes(t.status);
  });

  const onStatusFilterChange = (val) => {
    setStatusFilter(val);
    const todoStatus = mapStatusFilter[val];
    setFilterBy(todoStatus);
    if (isCPortal) {
      documentStore.loanTodosStore.setShowOnlyPending(TodoStatus.ALL !== todoStatus);
      loadTodos();
    }
  }

  const afterActionExecuted = () => {
    if (drawId) {
      drawStore.fetchDrawDTOById(drawId?.toString());

      documentStore.loanTodosStore.fetchTodos(loanId, LoanProcess.DRAWS, TaskV2Type.DRAW_ID, drawId);
    }
  }

  return (
    <Box>
      <Box pb={2} display="flex" justifyContent="flex-end">
        <Box pr={2}>
          <Tooltip title="Expand all sections" placement='bottom'>
            <div>
              <Button testId="expandAll" onClick={() => {
                setExpandAll(x => x + 1);
              }} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <UnfoldMore />&nbsp;&nbsp;Expand All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Collapse all sections" placement='bottom'>
            <div>
              <Button testId="collapseAll" onClick={() => {
                setCollapseAll(x => x + 1);
              }} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <UnfoldLess />&nbsp;&nbsp;Collapse All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Take screenshot" placement='bottom'>
            <div>
              <Button testId="downloadAll" onClick={download} variant="outlined" style={{
                letterSpacing: 'normal',
                textTransform: 'none',
              }}>
                <Box height={'31px'} display="flex" alignItems="center">
                  <PhotoCamera />&nbsp;&nbsp;Take Screenshot
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <StatusFilterField
          value={statusFilter}
          onChange={onStatusFilterChange}
          version="v2"
        />
      </Box>
      <PrintableArea ref={printableAreaRef}>
        <TaskTodoCardCompponent
          loanProcess={loanProcess}
          todos={filteredTodos}
          showSectionActions
          showDateColumn={true}
          showStatusColumn={true}
          showAssigneeColumn={isInternalPortal}
          showFileName
          expandAll={expandAll}
          collapseAll={collapseAll}
          showDownloadTodo
          showActions={true}
          filteredBy={filterBy}
          afterActionExecuted={afterActionExecuted}
        />
      </PrintableArea>
      <DrawDocumentForms loanId={loanId} drawId={drawId} />
    </Box>
  );
});
